/**
 * Privacy Settings Page (GDPR Data Rights)
 *
 * Allows users to:
 * - Manage cookie preferences
 * - Export their data
 * - Request account deletion
 */

import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';
import {
  getCookiePreferences,
  saveCookiePreferences,
  CookiePreferences,
} from '../../components/ui/CookieConsent';

export default function PrivacySettingsPage() {
  const { user } = useAuthStore();
  const [cookiePrefs, setCookiePrefs] = useState<CookiePreferences>(getCookiePreferences);

  // Export data state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const updateCookiePref = (key: 'functional' | 'analytics' | 'marketing', value: boolean) => {
    const newPrefs = { ...cookiePrefs, [key]: value };
    setCookiePrefs(newPrefs);
    saveCookiePreferences(newPrefs);
  };

  const handleExportData = async () => {
    setExportLoading(true);
    setExportError(null);
    setExportSuccess(false);

    try {
      const response = await api.post('/api/auth/export-data');

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `my-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 5000);
      } else {
        const data = await response.json() as { error?: string };
        setExportError(data.error || 'Failed to export data');
      }
    } catch {
      setExportError('Failed to connect to server');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      const response = await api.delete('/api/auth/me');

      if (response.ok) {
        // Clear local storage and redirect
        localStorage.clear();
        window.location.href = '/';
      } else {
        const data = await response.json() as { error?: string };
        setDeleteError(data.error || 'Failed to delete account');
      }
    } catch {
      setDeleteError('Failed to connect to server');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Privacy Settings</h1>

      {/* Cookie Preferences */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Cookie Preferences</h2>
        <p className="text-sm text-gray-500 mb-6">
          Manage how we use cookies on your device. Essential cookies are always enabled
          as they are required for the site to function.
        </p>

        <div className="space-y-4">
          {/* Essential */}
          <div className="flex items-center justify-between py-2">
            <div>
              <h3 className="font-medium text-gray-900">Essential Cookies</h3>
              <p className="text-sm text-gray-500">Required for authentication and security</p>
            </div>
            <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              Always Active
            </span>
          </div>

          {/* Functional */}
          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <h3 className="font-medium text-gray-900">Functional Cookies</h3>
              <p className="text-sm text-gray-500">Remember your preferences and settings</p>
            </div>
            <button
              onClick={() => updateCookiePref('functional', !cookiePrefs.functional)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                cookiePrefs.functional ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  cookiePrefs.functional ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Analytics */}
          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <h3 className="font-medium text-gray-900">Analytics Cookies</h3>
              <p className="text-sm text-gray-500">Help us improve by understanding usage patterns</p>
            </div>
            <button
              onClick={() => updateCookiePref('analytics', !cookiePrefs.analytics)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                cookiePrefs.analytics ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  cookiePrefs.analytics ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Marketing */}
          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <h3 className="font-medium text-gray-900">Marketing Cookies</h3>
              <p className="text-sm text-gray-500">Used for targeted advertising</p>
            </div>
            <button
              onClick={() => updateCookiePref('marketing', !cookiePrefs.marketing)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                cookiePrefs.marketing ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  cookiePrefs.marketing ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {cookiePrefs.consentDate && (
          <p className="mt-4 text-xs text-gray-400">
            Last updated: {new Date(cookiePrefs.consentDate).toLocaleString()}
          </p>
        )}
      </div>

      {/* Data Export */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Export Your Data</h2>
        <p className="text-sm text-gray-500 mb-4">
          Download a copy of all your personal data including your profile, forms, and submissions.
          This is your right under GDPR Article 20 (Right to Data Portability).
        </p>

        {exportError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {exportError}
          </div>
        )}

        {exportSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
            Your data export has been downloaded successfully.
          </div>
        )}

        <button
          onClick={handleExportData}
          disabled={exportLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {exportLoading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Preparing Export...
            </span>
          ) : (
            'Download My Data'
          )}
        </button>
      </div>

      {/* Delete Account */}
      <div className="bg-white rounded-lg shadow p-6 border-2 border-red-100">
        <h2 className="text-lg font-medium text-red-600 mb-4">Delete Account</h2>
        <p className="text-sm text-gray-500 mb-4">
          Permanently delete your account and all associated data. This action cannot be undone.
          Your forms and submissions will be permanently removed within 30 days.
        </p>

        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
          >
            Delete My Account
          </button>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium mb-2">
                This will permanently delete:
              </p>
              <ul className="text-red-700 text-sm space-y-1 ml-4 list-disc">
                <li>Your account and profile information</li>
                <li>All forms you have created ({user?.email})</li>
                <li>All form submissions and uploaded files</li>
                <li>API keys and integrations</li>
                <li>Group memberships</li>
              </ul>
            </div>

            {deleteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {deleteError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-mono font-bold">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="DELETE"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteLoading ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Legal Links */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Legal Documents</h3>
        <div className="flex gap-4 text-sm">
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
          <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
        </div>
      </div>
    </div>
  );
}
